import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
export class ExcelExportHandler {
  static async exportFlatTableToExcel(tableData: any[], tableHeaders: any[], tableName: string, sheetName: string, scenario_name: string, frozenColNumber: number): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = tableHeaders.map((header) => ({
      header: header.label,
      key: header.field,
      width: header?.data_type?.toLowerCase() === 'string' ? 40 : 30, // Default column width
    }));

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '2E2E38 ' }, // Purple background
      };
      cell.font = { bold: true, color: { argb: 'FFFFFF' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Add data rows and style them
    let previousGroupKey = null;
    let isGreyBackground = false;

    tableData.forEach((rowData, index) => {
      // Formatting scope for Interventions Table
      if (tableName.toLowerCase() === 'interventions') {
        Object.keys(rowData).forEach((key) => {
          if (key === 'scope') {
            if (['all_processing', 'all_packaging', 'all'].includes(rowData[key])) {
              let formattedScope = rowData[key].split('_').join(' ');
              rowData[key] = formattedScope.toUpperCase();
            }
          }
        });
      }

      const row = worksheet.addRow(rowData);

      // Construct the group key based on the first "frozenColNumber" columns
      const groupKey = tableHeaders
        .slice(0, frozenColNumber) // Get the first "frozenColNumber" columns
        .map((header) => rowData[header.field]) // Extract values from the row
        .join('|'); // Create a unique key for the group

      if (groupKey !== previousGroupKey) {
        isGreyBackground = !isGreyBackground; // Toggle the background for a new group
        previousGroupKey = groupKey;
      }

      row.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isGreyBackground ? 'EBEBEB' : 'FFFFFF' },
        };
        cell.font = { size: 12, bold: colNumber === 1 };
        cell.alignment = {
          horizontal: colNumber === 1 ? 'left' : 'center',
          vertical: 'middle',
          wrapText: colNumber === 1,
          indent: 1,
        };

        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    let fileName = `${scenario_name}_${tableName}`;
    // Save the file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  }

  static async exportFlatTableToExcelWithoutFormatting(tableData: any[], tableHeaders: any[], tableName: string, sheetName: string, scenario_name: string): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    worksheet.columns = tableHeaders.map((header) => ({
      header: header.label,
      key: header.field,
      width: 20,
    }));

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '2E2E38 ' }, // Purple background
      };
      cell.font = { bold: true, color: { argb: 'FFFFFF' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    tableData.forEach((rowData, index) => {
      worksheet.addRow(rowData);
    });

    let fileName = `${scenario_name}_${tableName}`;
    // Save the file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  }

  static async exportPivotTableToExcel(pivotData, tableHeaders, firstColumnHeader, rowGroup, fileName) {
    const flattenedData = this.flattenPivotTree(pivotData, tableHeaders);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Pivot Table Data');
    // Define columns dynamically based on rowGroup
    const columns =
      rowGroup.toLowerCase() !== 'all'
        ? [
            { header: firstColumnHeader, key: 'node_name', width: 30 },
            { header: 'Data Category', key: 'data_category', width: 30 },
            ...tableHeaders.map((header) => ({ header: header.header, key: header.field || '_empty', width: 25 })),
          ]
        : [{ header: firstColumnHeader, key: 'node_name', width: 30 }, ...tableHeaders.map((header) => ({ header: header.header, key: header.field || '_empty', width: 25 }))];
    worksheet.columns = columns;
    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '2E2E38 ' }, // Purple background
      };
      cell.font = { bold: true, color: { argb: 'FFFFFF' } }; // Bold white text
      cell.alignment = { horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
    let isGreyBackground = false;
    flattenedData.forEach((row) => {
      const isParentNode = !row.node_name.includes('>');
      // Extract parent node name and data category
      if (isParentNode) {
        if (rowGroup.toLowerCase() !== 'all') {
          // Empty all cells except node_name for parent nodes if rowGroup is not "all"
          Object.keys(row).forEach((key) => {
            if (key !== 'node_name') {
              row[key] = '';
            }
          });
        }
        row.data_category = ''; // No data category for parent nodes
      } else {
        const parts = row.node_name.split('>');
        row.node_name = parts[0].trim(); // Set only the parent node name
        row.data_category = parts[1] ? parts[1].trim() : ''; // Set the data category
      }
      // Ensure cells with 0 value are populated as a numeric 0
      Object.keys(row).forEach((key) => {
        if (row[key] === null || row[key] === undefined || row[key] === '' || row[key] == 0) {
          if (isParentNode && rowGroup.toLowerCase() !== 'all') {
            if (key !== 'node_name') {
              row[key] = '';
            }
          } else {
            row[key] = 0;
          }
        }
      });
      // if (isParentNode && rowGroup.toLowerCase() !== 'all') {
      //   // Add a blank row to separate parent nodes
      //   worksheet.addRow([]);
      // }
      // Handling empty keys [ with blank column header]
      Object.keys(row).forEach((key) => {
        if (key === '') {
          row['_empty'] = row[''];
        }
      });
      const excelRow = worksheet.addRow(row);
      // Apply styles for parent and child rows
      if (isParentNode) {
        // Parent node
        excelRow.getCell('node_name').font = { bold: true };
        isGreyBackground = !isGreyBackground; // Alternate background
      } else {
        // Child node
        excelRow.getCell('node_name').font = { italic: true, color: { argb: '000000' } };
        excelRow.getCell('data_category').font = { italic: true, color: { argb: '000000' } };
        excelRow.getCell('node_name').alignment = { indent: 1 };
      }
      // Apply row background and padding
      excelRow.eachCell((cell) => {
        // Alternate row background color
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isGreyBackground ? 'EBEBEB' : 'FFFFFF' }, // Alternate background
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: false,
          indent: 1,
        };
      });
      // Styling for first column
      excelRow.getCell('node_name').alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });
    // Save the file with the provided filename
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  }
  // Helper function to flatten the pivot data tree
  static flattenPivotTree(tree: any[], tableHeaders: any[], parentNodeName: string | null = null): any[] {
    const flattened: any[] = [];
    tree.forEach((node) => {
      // If parentNodeName is null or empty, just use the node's name. Otherwise, append to the parentNodeName.
      const currentRow: any = {
        node_name: parentNodeName === null ? node.node_name : parentNodeName === '' ? `- > ${node.node_name}` : `${parentNodeName} > ${node.node_name}`,
      };
      // Map year columns to the current node's data
      tableHeaders.forEach((header) => {
        currentRow[header.field] = node[header.field] || '';
      });
      flattened.push(currentRow);
      if (node.children && node.children.length > 0) {
        flattened.push(...this.flattenPivotTree(node.children, tableHeaders, currentRow.node_name));
      }
    });
    return flattened;
  }
}
